import { type ComponentType, type ReactNode, type SVGProps } from 'react'
import { BoxLogo, ClioLogo, DropboxLogo, GoogleDriveLogo, OneDriveLogo, SharePointLogo } from '@/assets/icons'
import { kExternalStorageProvider, kExternalStorageProviderDisplayName, type ExternalStorageProvider } from '@/constants/constants-external-storage'

export const PROVIDER_ICON_COMPONENTS: Record<ExternalStorageProvider, ComponentType<SVGProps<SVGSVGElement>>> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: GoogleDriveLogo,
  [kExternalStorageProvider.DROPBOX]: DropboxLogo,
  [kExternalStorageProvider.BOX]: BoxLogo,
  [kExternalStorageProvider.ONEDRIVE]: OneDriveLogo,
  [kExternalStorageProvider.CLIO]: ClioLogo,
  [kExternalStorageProvider.SHAREPOINT]: SharePointLogo,
}

/**
 * Shorter provider names for the Drive View source toggle, breadcrumb, and synced-file UI,
 * where "Microsoft OneDrive" reads as unnecessarily formal/long next to "Box" and "Dropbox".
 * Falls back to {@link kExternalStorageProviderDisplayName} for every other provider.
 */
export const PROVIDER_COMPACT_DISPLAY_NAME: Record<ExternalStorageProvider, string> = {
  ...kExternalStorageProviderDisplayName,
  [kExternalStorageProvider.ONEDRIVE]: 'OneDrive',
}

// Providers without an ID-based URL need a stored web_url before links can be offered.
const PROVIDER_FOLDER_URL_BUILDERS: Record<ExternalStorageProvider, (externalFolderId: string) => string | null> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: (id) => `https://drive.google.com/drive/folders/${id}`,
  [kExternalStorageProvider.BOX]: (id) => `https://app.box.com/folder/${id}`,
  [kExternalStorageProvider.ONEDRIVE]: () => null,
  [kExternalStorageProvider.DROPBOX]: () => null,
  [kExternalStorageProvider.CLIO]: () => null,
  // SharePoint ids are composite, site-scoped Graph ids
  // (`site:{site_id}:drive:{drive_id}:item:{item_id}`) that build no browsable URL,
  // and the ADR's source-link checklist rules out a provider homepage as a stand-in.
  // Graph does return a per-item `webUrl` and the adapter already captures it, so
  // this is unfinished Paxton plumbing (the stored web_url is not yet carried
  // through the imported-file contracts) rather than a SharePoint limitation —
  // tracked as a follow-up, same as OneDrive, Dropbox and Clio above.
  [kExternalStorageProvider.SHAREPOINT]: () => null,
}

// Drive open?id= routes both uploaded files and native Docs/Sheets to the appropriate viewer.
const PROVIDER_FILE_URL_BUILDERS: Record<ExternalStorageProvider, (externalFileId: string) => string | null> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: (id) => `https://drive.google.com/open?id=${id}`,
  [kExternalStorageProvider.BOX]: (id) => `https://app.box.com/file/${id}`,
  [kExternalStorageProvider.ONEDRIVE]: () => null,
  [kExternalStorageProvider.DROPBOX]: () => null,
  [kExternalStorageProvider.CLIO]: () => null,
  [kExternalStorageProvider.SHAREPOINT]: () => null,
}

export function getProviderFolderUrl(provider: ExternalStorageProvider, externalFolderId: string): string | null {
  return PROVIDER_FOLDER_URL_BUILDERS[provider](externalFolderId)
}

export function getProviderFileUrl(provider: ExternalStorageProvider, externalFileId: string): string | null {
  return PROVIDER_FILE_URL_BUILDERS[provider](externalFileId)
}

export const PROVIDER_META: Record<ExternalStorageProvider, { label: string; icon: ReactNode; modalIcon: ReactNode }> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: {
    label: kExternalStorageProviderDisplayName[kExternalStorageProvider.GOOGLE_DRIVE],
    icon: <GoogleDriveLogo className="h-[30px] w-[33px] shrink-0" />,
    modalIcon: <GoogleDriveLogo className="h-8 w-8" />,
  },
  [kExternalStorageProvider.DROPBOX]: {
    label: kExternalStorageProviderDisplayName[kExternalStorageProvider.DROPBOX],
    icon: <DropboxLogo className="h-[30px] w-[33px] shrink-0" />,
    modalIcon: <DropboxLogo className="h-8 w-8" />,
  },
  [kExternalStorageProvider.BOX]: {
    label: kExternalStorageProviderDisplayName[kExternalStorageProvider.BOX],
    icon: <BoxLogo className="h-[30px] w-[33px] shrink-0" />,
    modalIcon: <BoxLogo className="h-8 w-8" />,
  },
  [kExternalStorageProvider.ONEDRIVE]: {
    label: kExternalStorageProviderDisplayName[kExternalStorageProvider.ONEDRIVE],
    icon: <OneDriveLogo className="h-[30px] w-[33px] shrink-0" />,
    modalIcon: <OneDriveLogo className="h-8 w-8" />,
  },
  [kExternalStorageProvider.CLIO]: {
    label: kExternalStorageProviderDisplayName[kExternalStorageProvider.CLIO],
    icon: <ClioLogo className="h-[30px] w-[33px] shrink-0" />,
    modalIcon: <ClioLogo className="h-8 w-8" />,
  },
  [kExternalStorageProvider.SHAREPOINT]: {
    label: kExternalStorageProviderDisplayName[kExternalStorageProvider.SHAREPOINT],
    icon: <SharePointLogo className="h-[30px] w-[33px] shrink-0" />,
    modalIcon: <SharePointLogo className="h-8 w-8" />,
  },
}
